import { createSlice, createAsyncThunk, createEntityAdapter, createSelector } from '@reduxjs/toolkit'
import { PipelineService } from 'services/crm'

// Sort by `order` (set via drag-drop on the pipelines list page); fall back to
// name when the field is missing or two pipelines share an order value.
const pipelinesAdapter = createEntityAdapter({
  sortComparer: (a, b) => {
    const ao = a.order ?? Number.MAX_SAFE_INTEGER
    const bo = b.order ?? Number.MAX_SAFE_INTEGER
    if (ao !== bo) return ao - bo
    return a.name.localeCompare(b.name)
  },
})

export const fetchPipelines = createAsyncThunk(
  'crm/pipelines/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const res = await PipelineService.getAll(params)
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error fetching pipelines')
    }
  },
)

export const fetchPipeline = createAsyncThunk(
  'crm/pipelines/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const res = await PipelineService.get(id)
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error fetching pipeline')
    }
  },
)

export const createPipeline = createAsyncThunk(
  'crm/pipelines/create',
  async (data, { rejectWithValue }) => {
    try {
      const res = await PipelineService.create(data)
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error creating pipeline')
    }
  },
)

export const updatePipeline = createAsyncThunk(
  'crm/pipelines/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await PipelineService.update(id, data)
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error updating pipeline')
    }
  },
)

export const removePipeline = createAsyncThunk(
  'crm/pipelines/remove',
  async (id, { rejectWithValue }) => {
    try {
      await PipelineService.remove(id)
      return id
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error deleting pipeline')
    }
  },
)

export const reorderPipelines = createAsyncThunk(
  'crm/pipelines/reorder',
  async (pipelineIds, { rejectWithValue }) => {
    try {
      const res = await PipelineService.reorder(pipelineIds)
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error reordering pipelines')
    }
  },
)

const initialState = pipelinesAdapter.getInitialState({
  loading: 'idle',
  error: null,
})

const pipelinesSlice = createSlice({
  name: 'crmPipelines',
  initialState,
  reducers: {
    // Optimistic reorder for snappy drag-and-drop. Reducer assigns the new
    // `order` index to each id in the supplied array; the entity adapter's
    // sortComparer picks them up. The thunk re-syncs from the server.
    optimisticReorder(state, action) {
      const ids = action.payload
      ids.forEach((id, idx) => {
        const entity = state.entities[id]
        if (entity) entity.order = idx
      })
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPipelines.pending, (state) => {
        state.loading = 'pending'
        state.error = null
      })
      .addCase(fetchPipelines.fulfilled, (state, action) => {
        state.loading = 'idle'
        pipelinesAdapter.setAll(state, action.payload)
      })
      .addCase(fetchPipelines.rejected, (state, action) => {
        state.loading = 'idle'
        state.error = action.payload
      })
      .addCase(fetchPipeline.fulfilled, (state, action) => {
        const { stages, ...pipeline } = action.payload
        pipelinesAdapter.upsertOne(state, pipeline)
      })
      .addCase(createPipeline.fulfilled, (state, action) => {
        pipelinesAdapter.upsertOne(state, action.payload)
      })
      .addCase(updatePipeline.fulfilled, (state, action) => {
        pipelinesAdapter.upsertOne(state, action.payload)
      })
      .addCase(removePipeline.fulfilled, (state, action) => {
        pipelinesAdapter.removeOne(state, action.payload)
      })
      .addCase(reorderPipelines.fulfilled, (state, action) => {
        pipelinesAdapter.setAll(state, action.payload)
      })
  },
})

export const { optimisticReorder } = pipelinesSlice.actions

const selectors = pipelinesAdapter.getSelectors((state) => state.crmPipelines)
export const selectAllPipelines = selectors.selectAll
export const selectPipelineById = selectors.selectById
export const selectPipelineIds = selectors.selectIds

export const selectDefaultPipeline = createSelector([selectAllPipelines], (all) =>
  all.find((p) => p.isDefault) || all[0] || null,
)

export default pipelinesSlice.reducer
